/* ResourcePage — one record: edit or create. One page for all eight reference entities.
 *
 * spec/03 archetype B, read like `/profile`:
 *
 *   page head   — NOT sticky: breadcrumb (list ▸ record) + title + status + the lifecycle action
 *   main column — one card per descriptor section, fields on the `.pt-fieldgrid` grid
 *   side rail   — identity card, Summary (the facts) and Data quality (what is missing, clickable)
 *   save bar    — bottom, and only while there is something to save
 *
 * There is no view/edit toggle and no sticky command bar. The page is always an open form, so the
 * measured-offset machinery archetype B otherwise needs is not paid for here — it has shipped
 * broken four times elsewhere and this page has nothing to hang it on.
 *
 * Rules that each cost time, and are not negotiable:
 * - **Two baselines**: `pristine` (a signature string) for the dirty check, `original`
 *   (`structuredClone`) for reverting. `JSON.parse(JSON.stringify(item))` destroys `Date` fields.
 * - **`canSave = isNew ? true : dirty`** — a pristine NEW record must still be savable.
 * - **Archived ≠ deactivated.** `getDeactivation().via` decides which verb the page says:
 *   `delete` → Archive / Restore, `patch` → Deactivate / Activate. Saying "deactivate" while
 *   sending a DELETE told the user the wrong thing about what happened to their record.
 * - A failed save focuses the **first invalid field**, not a button the re-render may have detached.
 */
import React, { Component } from "react";
import { Avatar, Breadcrumb, Button, Flex, Modal, Space, Tag, Typography } from "antd";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  MinusCircleOutlined,
  SaveOutlined,
  UndoOutlined,
  WarningOutlined,
} from "@ant-design/icons";

import Alert from "/src/util/alert";

import ResourceForm from "./ResourceForm";
import { emptyValueFor } from "./fields";
import { deleteItem, fetchItem, getDeactivation, isInactive, patchItem, saveItem } from "./useResource";
import { parseErrors } from "../../util/action";
import { POINTS_ADMIN, TRANSLATION_DOWNLOADER } from "../../config/lang";
import { initialsOf } from "../../component/table-cells";
import { dateTimeCreated } from "../../util/formatter";
import { fill, translate } from "../../util/util";
import { getStore } from "../../util/redux/store";

/* The dirty signature, not a plain `JSON.stringify`: an image field holds a `File`, which
   serialises to `{}` — so picking a new logo on an existing record left the form looking pristine
   and Save disabled. */
const signature = item =>
  JSON.stringify(item, (key, value) =>
    typeof File !== "undefined" && value instanceof File ? `File:${value.name}:${value.size}:${value.lastModified}` : value
  );

const seed = descriptor => {
  const item = {};
  descriptor.fields.forEach(f => {
    item[f.name] = emptyValueFor(f);
  });
  return item;
};

const isEmpty = v => v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length);

export default class ResourcePage extends Component {
  constructor(props) {
    super(props);
    this.state = { mode: "VIEW", isNew: false, item: {}, errors: {}, saving: false, pristine: "{}" };
    this.original = {};
  }

  get me() {
    return this.props.currentUser || getStore().getState().me;
  }
  get lang() {
    return this.me.language;
  }
  get listPath() {
    return "/" + this.props.descriptor.slug;
  }

  t = key => translate(POINTS_ADMIN, key, this.lang);

  fieldLabel = field => translate(POINTS_ADMIN, field.label, this.lang) || field.label;

  /* A data-quality issue reads as a sentence, in the rail and under the field alike — the mockup's
     "Name is required", not a red copy of the label the user is already looking at. */
  requiredText = field => fill(this.t("q_required"), { f: this.fieldLabel(field) });

  leave = () => this.props.history.replace(this.listPath);

  componentDidMount() {
    this._isMounted = true;
    const { id } = this.props.match.params;
    const { descriptor } = this.props;
    if (id === "new") {
      this.setBaseline(seed(descriptor), { mode: "EDIT", isNew: true });
    } else {
      fetchItem(descriptor, id, item => this.setBaseline(item || {}));
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  safeSetState = (...args) => {
    if (this._isMounted) this.setState(...args);
  };

  /** Whatever the server last confirmed becomes both baselines and the current item. */
  setBaseline = (item, extra = {}) => {
    this.original = structuredClone(item);
    if (this._isMounted) this.setState({ item, pristine: signature(item), errors: {}, ...extra });
  };

  get dirty() {
    return signature(this.state.item) !== this.state.pristine;
  }

  get canSave() {
    return this.state.isNew ? true : this.dirty;
  }

  /** The required fields this record has not filled in — the rail's Data quality list. */
  get missing() {
    return (this.props.descriptor.fields || []).filter(f => f.required && isEmpty(this.state.item[f.name]));
  }

  onChange = (name, value) => {
    this.setState(s => ({ item: { ...s.item, [name]: value }, errors: { ...s.errors, [name]: undefined } }));
  };

  /* The re-render after a failed save detaches whatever had focus, so focus goes to the field the
     user has to fix — `data-field` is set by ResourceForm. */
  focusField = name => {
    if (!name || typeof document === "undefined") return;
    const host = document.querySelector(`[data-field="${name}"]`);
    if (!host) return;
    host.scrollIntoView({ block: "center", behavior: "smooth" });
    const control = host.querySelector("input, textarea, .ant-select-selector");
    if (control && control.focus) control.focus();
  };

  submit = () => {
    const { descriptor } = this.props;
    const { item } = this.state;
    const wasNew = !item.id;

    const errors = {};
    this.missing.forEach(f => {
      errors[f.name] = this.requiredText(f);
    });
    if (Object.keys(errors).length) {
      Alert.warning(this.t("fix_first"), { position: "top-right" });
      this.setState({ errors }, () => this.focusField(Object.keys(errors)[0]));
      return;
    }

    this.setState({ saving: true });
    saveItem(descriptor, item, this.me, r => {
      if (r && r.id) {
        Alert.success(this.t("saved"), { position: "top-right" });
        /* Refetch rather than trust the write response, exactly as `runRemove` and `activate` do:
           the write serializer returns FK/M2M as BARE IDS, so seeding the form from `r` made every
           entity select and every nested field render its id — "10", "11" where names had been —
           until the page was reloaded (PICSELL-1574 #22, #34). `submit` was the one lifecycle action
           still missing this. A refetch also means the form shows what the server actually stored,
           which is how a silently-dropped nested field becomes visible instead of staying hidden
           behind optimistic local state. */
        this.setBaseline(r, { mode: "VIEW", isNew: false, saving: false });
        fetchItem(descriptor, r.id, it => it && it.id && this.setBaseline(it));
        if (wasNew) this.props.history.replace(`${this.listPath}/${r.id}`);
      } else if (r && typeof r.json === "function") {
        /* A 400 comes back as the raw Response (see action.js) — its body is the field errors. */
        parseErrors(r).then(parsed => {
          this.safeSetState({ errors: parsed.fields, saving: false }, () => this.focusField(Object.keys(parsed.fields)[0]));
          Alert.warning(parsed.message || this.t("save_error"), { position: "top-right" });
        });
      } else {
        this.safeSetState({ saving: false });
        Alert.warning(this.t("save_error"), { position: "top-right" });
      }
    });
  };

  /* Cancel reverts from the CLONE, never from the serialised baseline. Leaving a dirty form asks
     first — the design's `discard_confirm`. */
  cancel = () => {
    if (this.state.isNew) {
      this.props.history.replace(this.listPath);
      return;
    }
    if (!this.dirty) {
      this.setState({ mode: "VIEW", errors: {} });
      return;
    }
    Modal.confirm({
      title: this.t("discard_confirm"),
      okText: this.t("view_btn"),
      okButtonProps: { danger: true },
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", this.lang),
      onOk: () => this.setBaseline(structuredClone(this.original), { mode: "VIEW" }),
    });
  };

  /** Archive (DELETE, flips `is_removed`) or deactivate (PATCH one flag) — never conflated. */
  remove = () => {
    const deact = getDeactivation(this.props.descriptor);
    const archiving = !deact || deact.via === "delete";

    Modal.confirm({
      title: archiving ? this.t("confirm_archive") : this.t("confirm_deactivate"),
      content: this.recordTitle,
      okText: this.t(deact ? (archiving ? "archive_btn" : "deactivate_btn") : "delete_btn"),
      okButtonProps: { danger: true },
      /* `Modal.confirm` renders outside the tree, so it does not inherit AppShell's
         `ConfigProvider locale` — without this the buttons stay English. */
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", this.lang),
      onOk: () => this.runRemove(deact),
    });
  };

  runRemove = deact => {
    const { descriptor } = this.props;
    const { item } = this.state;

    if (deact && deact.via === "patch") {
      patchItem(descriptor, item.id, { [deact.field]: deact.inactiveValue }, r => {
        if (r && r.id) {
          Alert.success(this.t("deleted"), { position: "top-right" });
          // The write serializer returns FK/M2M as bare ids; refetch so nested objects (names)
          // render instead of raw ids, without needing a page refresh.
          fetchItem(descriptor, item.id, it => this.setBaseline(it || r));
        } else {
          Alert.warning(this.t("save_error"), { position: "top-right" });
        }
      });
      return;
    }

    deleteItem(descriptor, item.id, ok => {
      if (!ok) {
        Alert.warning(this.t("save_error"), { position: "top-right" });
        return;
      }
      Alert.success(this.t("deleted"), { position: "top-right" });
      /* An ARCHIVED record still exists and can be restored, so the page stays open on it. A
         hard-deleted one does not, so there is nothing left to show. */
      if (deact) fetchItem(descriptor, item.id, it => this.setBaseline(it || item));
      else this.props.history.replace(this.listPath);
    });
  };

  activate = () => {
    const { descriptor } = this.props;
    const deact = getDeactivation(descriptor);
    if (!deact) return;
    patchItem(descriptor, this.state.item.id, { [deact.field]: deact.activeValue }, r => {
      if (r && r.id) {
        Alert.success(this.t("saved"), { position: "top-right" });
        // Refetch so FK/M2M fields come back as nested objects (names), not bare ids.
        fetchItem(descriptor, this.state.item.id, it => this.setBaseline(it || r));
      } else {
        Alert.warning(this.t("save_error"), { position: "top-right" });
      }
    });
  };

  get recordTitle() {
    const { item, isNew } = this.state;
    if (isNew) return this.t("new_btn");
    return item.name || item.title || (item.id != null ? `#${item.id}` : "");
  }

  /* --------------------------------------------------------------- pieces */

  /** The soft warnings the descriptor declares whose condition is currently true (a real gap that
   *  does not block Save the way a required field does). */
  get warnings() {
    const { item } = this.state;
    return (this.props.descriptor.warnings || []).filter(w => w.when(item));
  }

  /** The rail, /profile-style: an id card (logo/tile + name + status), an account-facts list
   *  (metrics + created), and a data-quality card. All derived from the descriptor. */
  renderRail() {
    const { descriptor } = this.props;
    const { item, isNew } = this.state;
    const deact = getDeactivation(descriptor);
    const off = !isNew && deact && isInactive(item, deact);
    const archiving = !deact || deact.via === "delete";
    const errors = this.missing;
    const warnings = this.warnings;

    const imageField = (descriptor.fields || []).find(f => f.type === "image");
    const logoSrc = imageField && typeof item[imageField.name] === "string" && item[imageField.name] ? item[imageField.name] : null;
    /* A descriptor can override the shared initials avatar with a tinted 2-letter tile — points
       hues it by network. `identityLogo` returns `{ text, hue }`. */
    const tile = !logoSrc && typeof descriptor.identityLogo === "function" ? descriptor.identityLogo(item) : null;

    /* Facts = the descriptor's metrics (Visits / Last visit) then the record's own facts. */
    const facts = [];
    if (!isNew) (descriptor.metrics || []).forEach(m => facts.push({ key: m.key, label: this.t(m.labelKey), value: m.value(item, this.lang) }));
    if (item.code) facts.push({ key: "code", label: this.t("f_code"), value: item.code });
    if (item.created) facts.push({ key: "created", label: this.t("created_at"), value: dateTimeCreated(item) });

    return (
      <aside className="dp-rail">
        {/* Identity card — the record's face, like profile's `pf-idcard`. Not rendered for an
            unsaved record: there is no face yet. Every other thing in it is suppressed by `!isNew`
            already (no metrics, no status badge), so what remained was a card holding a tile of the
            first two letters of the NEW-button label and the word "Додати" under it — an empty
            block with a verb in it (PICSELL-1574 #24). The rail keeps its Data quality card, which
            for a new record is the list of required fields still to fill. */}
        {!isNew && (
          <section className="dp-card pt-idcard">
            {tile ? (
              <span className="pt-logo pt-logo--lg" style={{ "--h": tile.hue }} aria-hidden>
                {tile.text}
              </span>
            ) : (
              /* Initials, photo preferred — never an `icon` prop (it would short-circuit the
               initials; see `table-cells.js`). */
              <Avatar shape="square" size={72} src={logoSrc} className="pt-idcard__logo" style={{ fontSize: 24 }}>
                {initialsOf(this.recordTitle)}
              </Avatar>
            )}
            <div className="pt-idcard__name">{this.recordTitle}</div>
            {off ? (
              <span className="pt-badge pt-badge--off">{this.t(archiving ? "archived" : "inactive")}</span>
            ) : (
              <span className="pt-badge pt-badge--ok">{this.t("active")}</span>
            )}
          </section>
        )}

        {facts.length > 0 && (
          <section className="dp-card">
            <div className="dp-sec-head">
              <h2 className="dp-sec-title">{this.t("summary")}</h2>
            </div>
            <ul className="pt-facts">
              {facts.map(f => (
                <li key={f.key}>
                  <span className="pt-facts__k">{f.label}</span>
                  <span className="pt-facts__v">{f.value}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="dp-card">
          <div className="dp-sec-head">
            <h2 className="dp-sec-title">{this.t("data_quality")}</h2>
          </div>
          {errors.length || warnings.length ? (
            <Flex vertical align="flex-start" gap={2}>
              {/* Clicking an issue jumps to the field that has it. Errors (required) first, in
                  danger; soft warnings after, in warning. */}
              {errors.map(field => (
                <Button
                  key={field.name}
                  type="link"
                  size="small"
                  className="pt-issue pt-issue--err"
                  icon={<ExclamationCircleOutlined />}
                  onClick={() => this.focusField(field.name)}>
                  {this.requiredText(field)}
                </Button>
              ))}
              {warnings.map(w => (
                <Button
                  key={w.key}
                  type="link"
                  size="small"
                  className="pt-issue pt-issue--warn"
                  icon={<WarningOutlined />}
                  onClick={() => this.focusField(w.field)}>
                  {this.t(w.labelKey)}
                </Button>
              ))}
            </Flex>
          ) : (
            <Flex align="center" gap={8}>
              <CheckCircleOutlined className="pt-ok-icon" />
              <Typography.Text type="secondary">{this.t("q_complete")}</Typography.Text>
            </Flex>
          )}
        </section>
      </aside>
    );
  }

  render() {
    const { descriptor } = this.props;
    const { item, isNew, errors, saving } = this.state;
    const deact = getDeactivation(descriptor);
    /* No view/edit toggle: the detail page is ALWAYS an open form. Save is dirty-driven, in the
       bottom bar. (There used to be a `descriptor.readOnly` branch rendering a `<Descriptions>`
       view plus `descriptor.detailPanel` — no descriptor ever set the flag, so neither could
       render; both were deleted rather than left as a second layout to keep in sync.) */
    const off = !isNew && deact && isInactive(item, deact);
    const archiving = !deact || deact.via === "delete";
    /* The bottom save bar exists only while there is something to save — an unsaved edit or a new
       draft. Profile's signature element. */
    const showSavebar = isNew || this.dirty;

    return (
      <div className="shell-content">
        {/* Page head — NOT sticky (profile has no top command bar): breadcrumb, title + status,
            and the lifecycle action, which is the one control that belongs up here rather than in
            the dirty-only save bar. */}
        <div className="dp-head">
          <div className="dp-head__main">
            <Breadcrumb
              items={[
                { title: <Typography.Link onClick={() => this.leave()}>{this.t(descriptor.titleKey)}</Typography.Link> },
                { title: <strong>{this.recordTitle}</strong> },
              ]}
            />
            <div className="dp-head__titlerow">
              <h1 className="dp-title">{this.recordTitle}</h1>
              {off && <Tag className="dp-tag--off">{this.t(archiving ? "archived" : "inactive")}</Tag>}
            </div>
          </div>
          {!isNew && (
            <Space size={8} wrap>
              {off ? (
                <Button icon={<UndoOutlined />} onClick={this.activate}>
                  {this.t(archiving ? "restore_btn" : "activate_btn")}
                </Button>
              ) : (
                <Button danger icon={deact ? archiving ? <InboxOutlined /> : <MinusCircleOutlined /> : <DeleteOutlined />} onClick={this.remove}>
                  {this.t(deact ? (archiving ? "archive_btn" : "deactivate_btn") : "delete_btn")}
                </Button>
              )}
            </Space>
          )}
        </div>

        <div className="dp-grid">
          <div className="dp-main">
            <ResourceForm descriptor={descriptor} item={item} errors={errors} onChange={this.onChange} lang={this.lang} />
          </div>

          {this.renderRail()}

          {/* Bottom save bar — spans the grid, sticks to the viewport bottom (under the app bar's
              top:0/z:30), appears only when dirty. */}
          {showSavebar && (
            <div className="dp-savebar pt-savebar">
              <span className="dp-savebar__msg">
                <EditOutlined />
                {isNew ? this.t("draft") : this.t("unsaved")}
              </span>
              <span className="dp-savebar__acts">
                <Button type="text" onClick={this.cancel} disabled={saving}>
                  {translate(TRANSLATION_DOWNLOADER, "cancel", this.lang)}
                </Button>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={!this.canSave} onClick={this.submit}>
                  {isNew ? this.t("create_btn") : this.t("save_btn")}
                </Button>
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
}
